import React from 'react';
import { ExternalLink, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { RESULTS_TABLE_CLASS } from './tableStyles';
import RootCausePicker from './RootCausePicker';
import LinkBugButton from './LinkBugButton';

/**
 * Tableau « Tests Filtrés » partagé par les huit pages équipe.
 *
 * Ces pages (Framework, DataExploration, DataPreparation, Investigation,
 * Agentique, Osint, RobotFramework, ReadyApi) sont des duplications du même
 * dashboard — voir CLAUDE.md. Chacune portait un ou deux exemplaires de ce
 * tableau, soit douze copies à maintenir en parallèle.
 *
 * Ce qui est réellement partagé (identique aux douze exemplaires, une fois
 * neutralisés le nom de variable de boucle `t`/`test` et l'indentation) :
 * la case à cocher de sélection, le statut, la suite, l'erreur, la colonne
 * « Bugs liés » (de loin le plus gros bloc), la cause racine, le statut de
 * relance et l'état vide.
 *
 * Ce qui variait vraiment, et reste donc à la charge de l'appelant :
 *  - la cellule « Nom » : texte brut sur les blocs de premier niveau, badge
 *    « New » + bouton ouvrant la modale Test Case sur les blocs de détail
 *    → `renderName`
 *  - la cellule « Actions » : lien `Log` direct vs bouton `Logs` qui va
 *    chercher le log complet, présence ou non de `Relancer`
 *    → `renderActions` (obligatoire)
 *
 * `StatusBadge` est injecté et non importé : les définitions de RobotFramework
 * et ReadyApi diffèrent de celle des six autres pages, les unifier ici
 * changerait leur rendu.
 *
 * Largeurs : le tableau est en `table-fixed` et remplit son conteneur ; voir
 * `COL_WIDTHS`. Les pages n'ont plus à surcharger de classes de largeur, ce qui
 * était la source des divergences (`max-w-[18rem]` ici, `whitespace-nowrap` là).
 *
 * Ordre des colonnes, commun aux douze exemplaires :
 *   [x] · Statut · Nom · [Suite] · [Env.] · Erreur · Bugs liés ·
 *   [Root Cause] · Actions · [Statut Relance]
 */

const TH_CLASS = 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase';
const TH_CLASS_TIGHT = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase';

/**
 * Largeur des colonnes « rigides », dont le contenu a une taille à peu près
 * fixe (badge, boutons, sélecteur de cause racine). `null` = colonne souple.
 *
 * Le tableau est en `table-fixed` : il occupe exactement la largeur de son
 * conteneur, et les colonnes souples (Nom et Erreur) se partagent ce qui reste
 * après les rigides. Le nombre de colonnes variant d'un bloc à l'autre, ce
 * partage est recalculé pour chaque tableau — c'est ce qui rend la largeur
 * dynamique au lieu de dépendre du contenu.
 *
 * En `table-auto` (l'ancien comportement), les colonnes s'élargissaient jusqu'à
 * dépasser le conteneur : la dernière (Actions / Statut Relance) sortait du
 * champ, et comme la barre de défilement horizontale reste invisible tant qu'on
 * ne la sollicite pas, le contenu paraissait simplement coupé.
 */
const COL_WIDTHS = {
  select: '2.5rem',
  status: '5.5rem',
  name: null,
  suite: '9rem',
  env: '4rem',
  error: null,
  bugs: '9.5rem',
  rootCause: '10rem',
  actions: '11rem',
  relance: '7rem',
};

export default function TestResultsTable({
  tests,
  /** Colonnes optionnelles : { suite, env, rootCause, relance }. */
  columns = {},
  /** « Nom » sur cinq pages, « Nom du Test » sur Agentique/RobotFramework/ReadyApi. */
  nameLabel = 'Nom',
  /** Badge de statut de la page appelante (trois variantes existantes). */
  StatusBadge,

  // Sélection multiple (barre de cause racine en masse)
  selectedTests,
  onSelectedTestsChange,

  // Colonnes Suite / Env.
  getSubSuite,
  /** Valeur unique affichée sur toutes les lignes (DataExploration). */
  envValue,

  // Colonne « Bugs liés »
  relatedBugsMap = {},
  nonReproMap = {},
  nonReproLoading = new Set(),
  /**
   * 'stacked'   : lien(s) bug ET contrôle non-reproduit empilés (onze blocs)
   * 'exclusive' : un seul contenu, par priorité (ReadyApi uniquement — un test
   *               en échec déjà rattaché à un bug n'y propose pas « Marquer non
   *               reproduit », et un test passé affiche la coche même s'il a des
   *               bugs). Comportement conservé tel quel.
   */
  bugsMode = 'stacked',
  onMarkNonRepro,
  onLinkBug,
  onShowBugs,

  // Colonne « Root Cause »
  rootCauseMap = {},
  notesMap = {},
  onRootCause,
  onNoteChange,
  onNoteBlur,

  // Colonne « Statut Relance »
  getRelanceStatus,

  // Cellules propres à la page
  renderName,
  renderActions,

  /** Bloc affiché à la place du tableau quand `tests` est vide. */
  emptyState,
}) {
  const { suite: showSuite, env: showEnv, rootCause: showRootCause, relance: showRelance } = columns;

  const failedTests = tests.filter(t => t.status === 'failed');

  // Ordre réel des colonnes de ce bloc, qui pilote le <colgroup>.
  const activeColumns = [
    'select',
    'status',
    'name',
    ...(showSuite ? ['suite'] : []),
    ...(showEnv ? ['env'] : []),
    'error',
    'bugs',
    ...(showRootCause ? ['rootCause'] : []),
    'actions',
    ...(showRelance ? ['relance'] : []),
  ];

  const toggleOne = (name) => {
    onSelectedTestsChange(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedTests.size === failedTests.length) onSelectedTestsChange(new Set());
    else onSelectedTestsChange(new Set(failedTests.map(t => t.name)));
  };

  const renderBugLink = (bug) => (
    <a
      href={bug.url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline flex items-center gap-1"
      title={bug.local ? 'Lien conservé côté app uniquement (aucun Test Case ADO à rattacher)' : undefined}
    >
      Bug #{bug.id} <ExternalLink className="w-3 h-3" />
      {bug.local && <span className="text-gray-400 italic">(local)</span>}
    </a>
  );

  const renderManyBugsButton = (test, bugs) => (
    <button onClick={() => onShowBugs(bugs, test.name)} className="text-blue-600 hover:underline">
      {bugs.length} bugs
    </button>
  );

  const renderNonReproButton = (test) => (
    <button
      onClick={() => onMarkNonRepro(test.name, test.id)}
      disabled={nonReproLoading.has(test.name)}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-orange-700 bg-orange-100 rounded hover:bg-orange-200 disabled:opacity-50"
      title="Marquer ce test comme non reproduit"
    >
      {nonReproLoading.has(test.name)
        ? <Loader2 className="w-3 h-3 animate-spin" />
        : <XCircle className="w-3 h-3" />}
      Marquer non reproduit
    </button>
  );

  const renderBugsCell = (test) => {
    const bugs = relatedBugsMap[test.name] || [];
    const isNonRepro = nonReproMap[test.name] === true;

    if (bugsMode === 'exclusive') {
      let mainContent;
      if (isNonRepro) {
        mainContent = <span className="text-gray-500 italic">Non reproduit</span>;
      } else if (test.status === 'failed' && bugs.length === 0) {
        mainContent = renderNonReproButton(test);
      } else if (test.status === 'passed') {
        mainContent = <CheckCircle className="w-5 h-5 text-green-600" />;
      } else if (bugs.length === 1) {
        mainContent = renderBugLink(bugs[0]);
      } else if (bugs.length > 1) {
        mainContent = renderManyBugsButton(test, bugs);
      } else {
        mainContent = <span className="text-gray-400">-</span>;
      }
      return (
        <div className="space-y-1">
          {mainContent}
          <LinkBugButton disabled={test.status !== 'failed'} onLink={(bugId) => onLinkBug(test.name, bugId)} />
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {bugs.length === 1 && renderBugLink(bugs[0])}
        {bugs.length > 1 && renderManyBugsButton(test, bugs)}
        {isNonRepro ? (
          <span className="text-gray-600 italic text-xs">Non reproduit</span>
        ) : test.status === 'failed' ? (
          renderNonReproButton(test)
        ) : bugs.length === 0 ? (
          <CheckCircle className="w-5 h-5 text-green-600" />
        ) : null}
        <LinkBugButton disabled={test.status !== 'failed'} onLink={(bugId) => onLinkBug(test.name, bugId)} />
      </div>
    );
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className={`${RESULTS_TABLE_CLASS} w-full table-fixed`}>
          <colgroup>
            {activeColumns.map(key => (
              <col key={key} style={COL_WIDTHS[key] ? { width: COL_WIDTHS[key] } : undefined} />
            ))}
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 cursor-pointer"
                  ref={el => {
                    if (!el) return;
                    el.checked = failedTests.length > 0 && selectedTests.size === failedTests.length;
                    el.indeterminate = selectedTests.size > 0 && selectedTests.size < failedTests.length;
                  }}
                  onChange={toggleAll}
                />
              </th>
              <th className={TH_CLASS}>Statut</th>
              <th className={TH_CLASS}>{nameLabel}</th>
              {showSuite && <th className={TH_CLASS}>Suite</th>}
              {showEnv && <th className={TH_CLASS}>Env.</th>}
              <th className={TH_CLASS}>Erreur</th>
              <th className={TH_CLASS}>Bugs liés</th>
              {showRootCause && <th className={TH_CLASS_TIGHT}>Root Cause</th>}
              <th className={TH_CLASS}>Actions</th>
              {showRelance && <th className={TH_CLASS}>Statut Relance</th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tests.map(test => (
              <tr key={test.id} className="hover:bg-gray-50">
                <td className="px-3 py-4 text-center">
                  {test.status === 'failed' && (
                    <input
                      type="checkbox"
                      checked={selectedTests.has(test.name)}
                      onChange={() => toggleOne(test.name)}
                      className="rounded border-gray-300 text-blue-600 cursor-pointer"
                    />
                  )}
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={test.status} />
                </td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words">
                  {renderName ? renderName(test) : test.name}
                </td>
                {showSuite && (
                  <td className="px-6 py-4 text-sm text-gray-500 break-words">
                    {getSubSuite(test) || '-'}
                  </td>
                )}
                {showEnv && (
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <span className="font-medium text-blue-600">{envValue || 'N/A'}</span>
                  </td>
                )}
                <td className="px-6 py-4 text-sm text-gray-500 truncate">
                  {test.error
                    ? <span title={test.error} className="text-red-600">{test.error}</span>
                    : <span className="text-green-600">-</span>}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {renderBugsCell(test)}
                </td>
                {showRootCause && (
                  <td className="px-4 py-4">
                    <RootCausePicker
                      value={rootCauseMap[test.name]}
                      onChange={(cause) => onRootCause(test.name, cause)}
                      disabled={test.status !== 'failed'}
                      note={notesMap[test.name] || ''}
                      onNoteChange={(val) => onNoteChange(test.name, val)}
                      onNoteBlur={(val) => onNoteBlur(test.name, val)}
                    />
                  </td>
                )}
                <td className="px-6 py-4 text-sm">
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    {renderActions(test)}
                  </div>
                </td>
                {showRelance && (
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {getRelanceStatus(test.name)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tests.length === 0 && emptyState}
    </>
  );
}
